import { useSearchParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { LoadingSpinner, Typography } from '@matillion/component-library'
import classes from './InterstitialAuthRedirectPage.module.scss'
import { Buffer } from 'buffer'
import { GIT_AUTH_EVENT_ID } from '../AuthMessageListener/types'
import i18n from 'i18n'

const InterstitialAuthRedirectPage = () => {
  const [searchParams] = useSearchParams()
  const [hasMissingState, setHasMissingState] = useState(false)
  const [hasMissingCode, setHasMissingCode] = useState(false)

  useEffect(() => {
    // The state value is '{RANDOM_STRING}_BASE64({ORGANISATION SUBDOMAIN})'
    const state = searchParams.get('state')

    // The code is a random value generated by the auth provider
    const code = searchParams.get('code')

    if (!state) {
      setHasMissingState(true)
      return
    }

    if (!code) {
      setHasMissingCode(true)
      return
    }

    // The second half of the state, after the underscore, is the BASE64 encoded organisation
    const encodedOrganisation = state.substring(state.indexOf('_') + 1)

    // We then decode the account back to its raw value
    const account = Buffer.from(encodedOrganisation, 'base64')

    // Rebuild the origin domain using the organisation as the subdomain
    const originWithSubdomain = window.origin.replace(
      /^https:\/\//,
      `https://${account}.`
    )

    // Post a message with our state and code back to the origin
    window.opener.postMessage(
      {
        code,
        state,
        type: GIT_AUTH_EVENT_ID
      },
      originWithSubdomain
    )

    // Close itself so the browser switches back to the origin tab
    window.close()
  }, [searchParams])

  const id = 'git-auth-interstitial'

  if (hasMissingCode || hasMissingState) {
    return (
      <div
        className={classes.wrapper}
        data-testid="git-oauth-redirect-interstitial-error"
      >
        <Typography
          format="tm"
          className={classes.title}
          data-testid={`${id}-error-title`}
        >
          {i18n.t('auth.interstitial.error-title')}
        </Typography>

        {hasMissingState && (
          <Typography format="bcm" data-testid={`${id}-missing-state`}>
            {i18n.t('auth.interstitial.missing-state')}
          </Typography>
        )}

        {hasMissingCode && (
          <Typography format="bcm" data-testid={`${id}-missing-code`}>
            {i18n.t('auth.interstitial.missing-code')}
          </Typography>
        )}

        <Typography
          format="bcm"
          className={classes.help}
          data-testid={`${id}-error-help`}
        >
          {i18n.t('auth.interstitial.error-help')}
        </Typography>
      </div>
    )
  }

  return (
    <div
      className={classes.wrapper}
      data-testid="git-oauth-redirect-interstitial"
    >
      <LoadingSpinner className={classes.loading} />

      <div className={classes.body}>
        <Typography
          format="tm"
          data-testid={`${id}-title`}
          className={classes.title}
        >
          {i18n.t('auth.interstitial.title')}
        </Typography>

        <Typography
          format="bcm"
          data-testid={`${id}-desc`}
          className={classes.desc}
        >
          {i18n.t('auth.interstitial.desc')}
        </Typography>

        <Typography
          format="bcm"
          data-testid={`${id}-info`}
          className={classes.info}
        >
          {i18n.t('auth.interstitial.info')}
        </Typography>
      </div>
    </div>
  )
}

export default InterstitialAuthRedirectPage
